import type { TeamOfficeGetResponse } from "@bezoned/shared/types/api/team/team-office-get.types.js";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import {
  teamLogoDelete,
  teamLogoUpdate,
} from "../../../../services/api.service.js";
import { recordErrorInSentry } from "../../../../utils/init-sentry.js";
import { TeamLogoDropzone } from "../../tabs/theme/components/team-logo-dropzone.js";

interface TeamEditLogoSectionProps {
  msTeamId: string;
  onChange: () => void;
  onError: (error: unknown) => void;
  teamQueryData: TeamOfficeGetResponse | undefined;
}

export const TeamEditLogoSection = ({
  msTeamId,
  onChange,
  onError,
  teamQueryData,
}: TeamEditLogoSectionProps) => {
  const [logoChange, setLogoChange] = useState<File | null | undefined>();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const editMutation = useMutation({
    mutationFn: ({ logoFile }: { logoFile?: File; logoUrl?: null }) =>
      logoFile
        ? teamLogoUpdate({ logoFile, msTeamId })
        : teamLogoDelete({ msTeamId }),
    onError: (error) => {
      recordErrorInSentry(error);
      onError(error);
    },
    onSuccess: () => {
      setLogoChange(undefined);
      setErrorMessage(null);
      queryClient.invalidateQueries({ queryKey: ["teamOfficeGet", msTeamId] });
      onChange();
    },
  });

  const handleSelectedFileChange = (file: File | null) => {
    if (!file) {
      setLogoChange((previousValue) =>
        previousValue instanceof File ? undefined : previousValue
      );
      return;
    }

    setLogoChange(file);
    editMutation.mutate({ logoFile: file });
  };

  const handleLogoDelete = () => {
    setLogoChange(null);
    editMutation.mutate({ logoUrl: null });
  };

  return (
    <TeamLogoDropzone
      disabled={editMutation.isPending}
      errorMessage={errorMessage}
      onDeleteChange={handleLogoDelete}
      onErrorMessageChange={setErrorMessage}
      onSelectedFileChange={handleSelectedFileChange}
      previewUrl={logoChange === null ? null : (teamQueryData?.logoUrl ?? null)}
    />
  );
};
